<template>
  <div class="saldos-table mb-4" v-if="totalElements > 0">
    <PColapsable>
      <template #title>
        <div class="titleColaps_ColapsableSalidasTable">
          <span class="cod-divisa-label">{{ divisa.codDivisa }}</span>
          <span class="divisa-total">{{ filterNumber(totalSaldoActual, 2) }}</span>
        </div>
      </template>
      <b-table
        :fields="visibleFields"
        :items="saldos"
        :sort-by.sync="pagination.orderBy"
        :sort-desc.sync="sortDesc"
        sort-icon-left
        :empty-text="$t('label.noData')"
        ref="tableSaldos"
        show-empty
        no-border-collapse
        borderless
        responsive
        @sort-changed="onSortingChange"
      >
        <div class="text-center loader my-2" slot="table-busy">
          <b-spinner class="align-middle" />
        </div>

        <template #head(actions-right)>
          <select-columns :fields.sync="fields" :pagination="pagination" :cod-table="codTable" />
        </template>

        <!-- Left Actions Btns -->
        <template #cell(actions-left)="row">
          <div class="table-button-container d-flex">
            <b-icon
              class="icon-details ml-3 my-auto cursor-pointer"
              icon="chevron-down"
              aria-label="Collapse Toggle"
              :flip-v="row.detailsShowing"
              @click="row.toggleDetails"
            />
            <img
              src="@/assets/icons/entradas-icon.svg"
              :alt="$t('label.entradas')"
              class="icon-entradas ml-3 my-auto cursor-pointer position-relative"
              v-b-tooltip.hover.d300="$t('label.entradas')"
              @click="openModalEntradas(row.item)"
            />
            <img
              src="@/assets/icons/salidas-icon.svg"
              :alt="$t('label.salidas')"
              class="icon-salidas ml-3 my-auto cursor-pointer"
              v-b-tooltip.hover.d300="$t('label.salidas')"
              @click="openModalSalidas(row.item)"
            />
          </div>
        </template>

        <!-- Right Actions Btns -->
        <template #cell(actions-right)="row">
          <div class="table-button-container" v-if="row.item.diferencias.length">
            <img
              :id="`diff-${divisa.codDivisa}-${row.index}`"
              src="@/assets/icons/diferencia-icon.svg"
              :alt="$t('label.diferencias')"
              class="icon-diferencia"
            />

            <b-tooltip
              :target="`diff-${divisa.codDivisa}-${row.index}`"
              custom-class="table-cell-tooltip"
              placement="bottom"
            >
              <template v-for="(diferencia, index) in row.item.diferencias">
                <div v-if="index < 7" :key="diferencia.id">
                  <hr v-if="index" class="m-0" />

                  <div class="tooltip-inner-content-diff">
                    <div>
                      {{ $t("label.declarado") }}:
                      {{ filterNumber(diferencia.entrada.numDeclarado, 2) }}
                    </div>
                    <div>
                      {{ $t("label.diferencia") }}:
                      {{ filterNumber(diferencia.entrada.numDiferencia, 2) }}
                    </div>
                  </div>
                </div>
                <div v-if="index === 8" :key="diferencia.id">
                  <hr v-if="index" class="m-0" />
                  <div class="tooltip-inner-content-diff">
                    <div class="text-center">...</div>
                  </div>
                </div>
              </template>
            </b-tooltip>
          </div>
        </template>

        <template #cell(fecCertificado)="row">
          <span :id="`fecCertificado-${divisa}-${row.index}`">
            {{ filterLocalDateTime(row.item.fecCertificado) }}
          </span>
        </template>

        <template #row-details="row">
          <b-card class="detail-row">
            <DetalleSaldo :saldo="row.item" :columnsConfig="detalleSaldosConfig" />
          </b-card>
        </template>
      </b-table>
      <div class="mt-3" v-if="totalElements > pagination.size">
        <pagination
          :current-page="pagination.page"
          :page-size="pagination.size"
          :total-elements="totalElements"
          @change="onChangeCurrentPage"
        />
      </div>
    </PColapsable>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import VueFilters from "@/vue-filters";
import DetalleSaldo from "./DetalleSaldo.vue";
import Pagination from "@/components/common/Pagination.vue";
import { IConfColumnaPais, IDivisa, IField, ISaldo, ISoConfColumnasUpdateDTO } from "@/entities";
import { Action } from "vuex-class";
import { EventBus } from "@/event-bus";
import SelectColumns from "@/components/common/SelectColumns/SelectColumns.vue";
import {
  EDirection,
  IColumnaResponse,
  IMovimientoParams,
  IPaged,
  ISaldoData,
  ISaldoParams,
} from "@/services";

@Component({
  name: "saldos-table",
  components: {
    SelectColumns,
    DetalleSaldo,
    Pagination,
  },
  mixins: [VueFilters],
})
export default class ColapsableTodosTable extends Vue {
  @Prop({ required: true }) divisa!: IDivisa;
  @Prop({ required: true }) params!: IMovimientoParams;
  @Prop({ required: true, default: {} }) detalleSaldosConfig!: IColumnaResponse;
  @Prop({ required: false, default: false }) isBCR!: boolean;

  @Watch("params", { deep: true })
  onChangeParams(): void {
    this.pagination.page = 1;
    this.getSaldos();
  }

  @Prop({ required: true }) columnsConfig!: IColumnaResponse;
  @Action("fetchSaldos") fetchSaldosPage!: (
    params: ISaldoParams
  ) => Promise<ISaldoData | undefined>;
  @Action("fetchSaldosBCR") fetchSaldosBCRPage!: (
    params: ISaldoParams
  ) => Promise<ISaldoData | undefined>;
  @Action("saveColumna") saveColumna!: (
    params: ISoConfColumnasUpdateDTO
  ) => Promise<IColumnaResponse | undefined>;
  // Is card table collapsed
  collapsed = true;
  codTable = "saldos";

  sortDesc = this.columnsConfig?.codTipoOrden !== "ASC";
  totalElements = 0;

  pagination: IPaged = {
    direction: this.sortDesc ? EDirection.DESC : EDirection.ASC,
    orderBy: this.columnsConfig?.codOrden ?? "fecCertificado",
    page: 1,
    size: 10,
  };

  saldos: ISaldo[] = [];
  totalSaldoActual = 0;

  get fields(): IField[] {
    const visibleKeys = this.columnsConfig?.codColumna?.split(",").filter((s) => s !== "") || [];
    let columnas: IField[] = [
      {
        key: "actions-left",
        label: this.$t("label.acciones"),
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first actions",
        visible: true,
        class: "table-cell",
        aux: true,
        code: "",
      },
    ];
    this.columnsConfig.columnas?.forEach((column: IConfColumnaPais) => {
      columnas.push({
        key: column.keyFront,
        label: this.$t("label." + column.codColumna),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        thStyle: column.widthFront ? { width: column.widthFront } : {},
        visible: this.isVisible(visibleKeys, column.codColumna),
        class: "table-cell",
        aux: false,
        code: column.codColumna,
        formatter: (value: any): string => {
          if (column.formatType == "Date") {
            return this.filterLocalDate(value);
          } else if (column.formatType == "Number") {
            return this.filterNumber(value, 2);
          }
          return value;
        },
      });
    });
    columnas.push({
      key: "actions-right",
      label: "",
      sortable: false,
      thClass: "table-header table-header-add-columnas pb-0",
      tdClass: "table-cell-middle actions",
      visible: true,
      class: "table-cell",
      aux: true,
      code: "",
    });
    return columnas;
  }

  set fields(fields: IField[]) {
    this.columnsConfig.codColumna =
      fields
        .filter((item) => item.visible && !item.aux)
        .map((item) => item.code)
        .join(",") || "";
  }

  get visibleFields(): IField[] {
    return this.fields.filter((item) => item.visible);
  }

  mounted(): void {
    this.getSaldos();
  }

  async getSaldos(): Promise<void> {
    const params: IMovimientoParams = { ...this.params };
    params.pkDivisa = this.divisa.id;
    params.page = this.pagination.page! - 1;
    params.size = this.pagination.size;
    params.orderBy = this.pagination.orderBy;
    params.direction = this.pagination.direction;
    let response: ISaldoData | undefined;
    if (this.isBCR) {
      response = await this.fetchSaldosBCRPage(params);
    } else {
      response = await this.fetchSaldosPage(params);
    }
    if (response) {
      this.saldos = response.saldos.content;
      this.totalSaldoActual = response.totalSaldo;
      this.totalElements = response.saldos.totalElements;
    }
  }

  isVisible(visibleKeys: string[], column: string): boolean {
    return !visibleKeys.length || !!visibleKeys.find((key) => key === column);
  }

  onChangeCurrentPage(currentPage: number): void {
    this.pagination.page = currentPage;
    this.getSaldos();
  }

  onSortingChange(): void {
    this.$nextTick(async () => {
      this.pagination.direction = this.sortDesc ? EDirection.DESC : EDirection.ASC;
      await this.getSaldos();
      await this.saveColumnsConfig();
    });
  }

  openModalEntradas(saldo: ISaldo): void {
    EventBus.$emit("open-modal-entradas", saldo);
  }

  openModalSalidas(saldo: ISaldo): void {
    EventBus.$emit("open-modal-salidas", saldo);
  }

  async saveColumnsConfig(): Promise<void> {
    const params: ISoConfColumnasUpdateDTO = {
      codColumna: this.columnsConfig.codColumna || "",
      codTabla: this.columnsConfig.codTabla,
    };
    if (this.pagination) {
      params.codTipoOrden = this.pagination.direction;
      params.numPagElements = this.pagination.size;
      params.codOrden = this.pagination.orderBy;
    }
    await this.saveColumna(params);
  }
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

.toggle-separator {
  border-top: solid 1px #e9e9e9;
}

.cod-divisa-label {
  margin-left: 5px;
  margin-right: 9px;
  font-size: 14px;
  font-weight: 300;
  color: var(--secundario-prosegur-claro);
}

.divisa-total {
  font-size: 14px;
  font-weight: bold;
  color: var(--secundario-prosegur-claro);
}
</style>
